import jsPDF from 'jspdf';
import moment from 'moment';
import { EQuestionTypes } from '@modules/survey/definitions';
import type { FormSubmissionDetail, FormSubmissionAnswer, SubmissionStatus } from '../../types';
import type { Organisation } from '@common/types/objects';
import i18n from '../../../../i18n';
import { convertImageToBase64, imageUrlToDataUrl } from './pdf-image-utils';
import { QUESTION_TYPE_COLORS } from './question-type-constants';
import { getQuestionIconDataUrl, getChecklistTickDataUrl } from './pdf-icon-utils';

export interface ExportFormSubmissionPDFOptions {
  submission: FormSubmissionDetail;
  form: {
    id: string;
    title: string;
    icon?: string;
    color?: string;
  };
  user?: {
    full_name: string;
    id: string;
  } | null;
  submittedAt: string;
  status?: SubmissionStatus | null;
  org: Organisation;
  timezone?: string;
  index?: number;
}

const DEFAULT_PRIMARY_COLOR = '#2563EB';
const PAGE_HEIGHT = 842;
const FOOTER_HEIGHT = 50;
const PAGE_TOP_MARGIN = 24; // CSS-only padding for continuation pages - NOT a DOM element, just padding
const ONETEAM_LOGO = '/static/images/logo.png';

function escapeHtml(text: string): string {
  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
}

function getQuestionColor(type: string): string {
  return QUESTION_TYPE_COLORS[type] || '#6B7280';
}


// eslint-disable-next-line @typescript-eslint/no-unused-vars
function bytesToSize(bytes: number | undefined | null): string {
  if (typeof bytes !== 'number') return '-';

  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
  if (bytes === 0) return '0 Bytes';
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  const size = (bytes / (1024 ** i)).toFixed(1);
  return `${size} ${sizes[i]}`;
}

function formatAnswerForHTML(
  answer: FormSubmissionAnswer,
  t: (key: string, fallback?: string) => string,
  tickIconDataUrl?: string | null,
  imageDataUrlMap?: Map<string, string | null>,
  timezone?: string
): string {
  const { question, answer: value } = answer;

  if (value === null || value === undefined || value === '') {
    return `<span style="color:#9CA3AF;font-style:italic;">${t('survey:survey_response_no_answer', 'No answer')}</span>`;
  }

  switch (question.type) {
    case EQuestionTypes.DROPDOWN: {
      const { options } = question.parameters;
      if (!Array.isArray(value)) {
        return `<span style="color:#9CA3AF;font-style:italic;">${t('survey:survey_response_no_answer', 'No answer')}</span>`;
      }
      return (value as string[])
        .map((optionId: string) => {
          const option = options.find((o: any) => o.id === optionId);
          return option?.value;
        })
        .filter((o: any) => !!o)
        .join(', ');
    }

    case EQuestionTypes.BOOLEAN:
      if (value === null) {
        return `<span style="color:#9CA3AF;font-style:italic;">${t('survey:survey_response_no_answer', 'No answer')}</span>`;
      }
      return value === true ? question.parameters.label_true : question.parameters.label_false;

    case EQuestionTypes.DATE_TIME: {
      if (!value) {
        return `<span style="color:#9CA3AF;font-style:italic;">${t('survey:survey_response_no_answer', 'No answer')}</span>`;
      }
      const dateTimeObj = moment(value as string, ['YYYY-MM-DD HH:mm:ss', 'YYYY-MM-DD', 'HH:mm:ss']);
      const strings = [];
      if (question.parameters.allow_date_select) strings.push(dateTimeObj.format('MMMM D, YYYY'));
      if (question.parameters.allow_time_select) strings.push(dateTimeObj.format('h:mm A'));

      // Add timezone information if available - display full timezone name (e.g., "Asia/Calcutta")
      if (timezone) {
        // Use the timezone name directly to match web UI format
        strings.push(timezone);
      }

      return strings.join(', ');
    }

    case EQuestionTypes.FILE_UPLOAD: {
      if (!Array.isArray(value) || value.length === 0) {
        return `<span style="color:#9CA3AF;font-style:italic;">${t('survey:survey_response_no_answer', 'No answer')}</span>`;
      }

      // Match email implementation: show images with fixed height, file names only for non-images
      const fileItems = (value as any[]).map((attachment) => {
        // Trim file name to remove ALL extra spaces (especially before file extension and between words)
        const rawFileName = attachment.file_name || attachment.name || 'Unknown file';
        // Aggressively remove ALL extra spaces - normalize all whitespace
        // Convert to string first, then normalize all whitespace characters to regular spaces
        let fileName = String(rawFileName || '').trim();
        if (!fileName) fileName = 'Unknown file';

        // Remove all types of whitespace (including non-breaking spaces, zero-width spaces, etc.) and replace with single space
        fileName = fileName.replace(/[\s\u00A0\u2000-\u200B\u2028\u2029\uFEFF\u3000\u200C\u200D]+/g, ' ');
        // CRITICAL: Remove spaces before and after dots (file extensions) - do this multiple times to catch all cases
        fileName = fileName.replace(/\s+\./g, '.'); // Remove space before dot
        fileName = fileName.replace(/\.\s+/g, '.'); // Remove space after dot
        fileName = fileName.replace(/\s*\.\s*/g, '.'); // Remove any spaces around dot (catch any remaining)
        // Remove multiple consecutive spaces between words (do this multiple times to be thorough)
        fileName = fileName.replace(/\s{2,}/g, ' ');
        fileName = fileName.replace(/\s{2,}/g, ' '); // Second pass to catch any remaining
        // Final trim
        fileName = fileName.trim();

        const fileType = attachment.file_type || '';
        const isImage = fileType && (fileType.startsWith('image') || fileType === 'image' || fileType === 'gif');
        const attachmentId = attachment.id || '';

        // Get preloaded image data URL from map
        const imageDataUrl = isImage ? (imageDataUrlMap?.get(attachmentId) || null) : null;

        if (isImage && imageDataUrl) {
          // Show image with fixed height to prevent breaking PDF layout
          // Set to 100px height as requested
          // CRITICAL: Images must NEVER be cut - if image doesn't fit, move entire image to next page
          // Use table display to make the entire file item atomic (cannot be split)
          const imageOuterWrapperStyle = 'display:block;width:100%;'
            + 'page-break-inside:avoid !important;break-inside:avoid !important;'
            + '-webkit-column-break-inside:avoid !important;'
            + 'page-break-before:auto !important;break-before:auto !important;'
            + 'page-break-after:avoid !important;break-after:avoid !important;'
            + 'margin-bottom:6px;'
            + 'overflow:hidden;'
            + 'min-height:100px;'
            + 'height:auto;'
            + 'position:relative;';
          const imageInnerWrapperStyle = 'display:block;width:100%;'
            + 'page-break-inside:avoid !important;break-inside:avoid !important;'
            + '-webkit-column-break-inside:avoid !important;'
            + 'position:relative;';
          const imageStyle = 'max-width:100%;max-height:100px;width:auto;height:auto;'
            + 'object-fit:contain;display:block;border-radius:4px;'
            + 'margin:0;padding:0;'
            + 'page-break-inside:avoid !important;break-inside:avoid !important;'
            + '-webkit-column-break-inside:avoid !important;'
            + 'page-break-before:avoid !important;break-before:avoid !important;'
            + 'page-break-after:avoid !important;break-after:avoid !important;'
            + 'vertical-align:top;';

          return `
            <div class="file-item-wrapper file-item-image" style="${imageOuterWrapperStyle}">
              <div class="file-item-inner" style="${imageInnerWrapperStyle}">
                <img src="${imageDataUrl}" alt="${escapeHtml(fileName)}" style="${imageStyle}" 
                     crossorigin="anonymous" loading="eager" />
              </div>
            </div>`;
        }
          // Show file name only (no icon, no box, no size) - matching email format
          // CRITICAL: File names must NEVER be cut - if file name doesn't fit, move entire file name to next page
          const fileNameStyle = 'font-size:12px;font-weight:500;color:#111827;'
            + 'word-wrap:break-word;overflow-wrap:break-word;'
            + 'display:block;margin:0;padding:0;line-height:1.4;margin-bottom:6px;'
            + 'letter-spacing:0;word-spacing:0;font-kerning:none;'
            + 'white-space:normal;word-break:normal;'
            + 'page-break-inside:avoid !important;break-inside:avoid !important;'
            + '-webkit-column-break-inside:avoid !important;'
            + 'page-break-before:auto !important;break-before:auto !important;'
            + 'page-break-after:auto !important;break-after:auto !important;';

          return `<div class="file-name-item" style="${fileNameStyle}">${escapeHtml(fileName)}</div>`;

      });

      // Wrap all file items in a container that maintains padding when breaking across pages
      // Top margin is handled by PDF margin parameter (applies to all pages)
      return `<div class="file-items-container" style="padding:0;margin:0;line-height:1.4;">${fileItems.join('')}</div>`;
    }

    case EQuestionTypes.CHECKLIST:
    case EQuestionTypes.MULTIPLE_CHOICE: {
      const arr = Array.isArray(value) ? value : [value];
      const items = question.parameters.options
        .filter((option: any) => arr.includes(option.id))
        .map((option: any) => {
          if (tickIconDataUrl) {
            const tickIconStyle = 'width:10px;height:10px;display:inline-block;'
              + 'vertical-align:middle;margin-right:6px;'
              + 'image-rendering:auto;object-fit:contain;';
            const optionTextStyle = 'font-size:13px;color:#374151;vertical-align:middle;line-height:1.4;'
              + 'letter-spacing:0;word-spacing:0;font-kerning:none;'
              + 'white-space:normal;word-break:normal;overflow-wrap:normal;';
            return `<div style="margin:4px 0;">
              <img src="${tickIconDataUrl}" style="${tickIconStyle}" alt="" />
              <span style="${optionTextStyle}">${escapeHtml(option.value)}</span>
            </div>`;
          }
            const bulletStyle = 'display:inline-block;width:10px;height:10px;'
              + 'background:#6E6E73;border-radius:50%;text-align:center;line-height:10px;'
              + 'color:white;font-size:7px;vertical-align:middle;margin-right:6px;';
            const optionTextStyle2 = 'font-size:13px;color:#374151;vertical-align:middle;line-height:1.4;'
              + 'letter-spacing:0;word-spacing:0;font-kerning:none;'
              + 'white-space:normal;word-break:normal;overflow-wrap:normal;';
            return `<div style="margin:4px 0;">
              <span style="${bulletStyle}">&#10003;</span>
              <span style="${optionTextStyle2}">${escapeHtml(option.value)}</span>
            </div>`;

        });
      return items.join('');
    }

    case EQuestionTypes.SLIDER:
    case EQuestionTypes.NUMBER: {
      if (!value && value !== 0) {
        return `<span style="color:#9CA3AF;font-style:italic;">${t('survey:survey_response_no_answer', 'No answer')}</span>`;
      }
      return String(value);
    }

    default: {
      const noAnswerStyle = 'color:#9CA3AF;font-style:italic;';
      const noAnswerHtml = escapeHtml(String(value))
      || `<span style="${noAnswerStyle}">${t('survey:survey_response_no_answer', 'No answer')}</span>`;
      return noAnswerHtml;
    }
  }
}

interface GenerateHTMLOptions {
  options: ExportFormSubmissionPDFOptions;
  orgLogoDataURL?: string;
  formIconDataURL?: string;
  answersHtml: string;
}

function generateSubmissionHTML({
  options,
  orgLogoDataURL,
  formIconDataURL,
  answersHtml,
}: GenerateHTMLOptions): string {
  const {
 form, user, submittedAt, status, org, index
} = options;
  const t = (key: string, fallback?: string) => i18n.t(key, fallback) as string;
  const primaryColor = org.theme?.main_color || DEFAULT_PRIMARY_COLOR;

  const userName = user
    ? `${user.full_name} (#${index || 0})`
    : `${t('forms:anonymous', 'Anonymous')} (#${index || 0})`;
  const formattedDate = moment(submittedAt).format('MMM D, YYYY, HH:mm');

  const formIconBgColor = form.color || primaryColor;
  const formIconContainerStyle = 'display:inline-block;width:24px;height:24px;'
    + `background-color:${formIconBgColor};border-radius:4px;vertical-align:middle;`
    + 'margin-right:12px;position:relative;';
  const formIconImgStyle = 'position:absolute;top:4px;left:4px;width:16px;height:16px;'
    + 'border:none;display:block;';
  const formIconEmptyStyle = 'display:inline-block;width:24px;height:24px;'
    + `background-color:${formIconBgColor};border-radius:4px;vertical-align:middle;`
    + 'margin-right:12px;';
  const formIconHtml = formIconDataURL
    ? `<div style="${formIconContainerStyle}">
        <img src="${formIconDataURL}" crossorigin="anonymous" style="${formIconImgStyle}" />
      </div>`
    : `<div style="${formIconEmptyStyle}"></div>`;

  const orgLogoImgStyle = 'height:24px;max-width:24px;border:none;vertical-align:middle;'
    + 'margin-left:12px;border-radius:4px;';
  const orgLogoHtml = orgLogoDataURL
    ? '<span style="font-size:14px;font-weight:500;color:#ffffff;vertical-align:middle;">'
      + `${escapeHtml(org.name)}</span>
       <img src="${orgLogoDataURL}" crossorigin="anonymous" style="${orgLogoImgStyle}" />`
    : `<span style="font-size:14px;font-weight:500;color:#ffffff;">${escapeHtml(org.name)}</span>`;

  const statusDotStyle = `display:inline-block;width:12px;height:12px;background:${status?.color};`
    + 'border-radius:50%;vertical-align:middle;margin-right:6px;';
  const statusTextStyle = 'font-size:12px;font-weight:600;color:#ffffff;vertical-align:middle;';
  const statusHtml = status
    ? `<span style="${statusDotStyle}"></span>
       <span style="${statusTextStyle}">${escapeHtml(status.name)}</span>`
    : '<span style="font-size:12px;color:#ffffff;">-</span>';

  // Use regular space for consistent canvas rendering (non-breaking spaces cause spacing artifacts)
  const submittedAtText = t('forms:submitted_at', 'Submitted at');

  // Extract long inline styles to variables to avoid max-len errors
  // CRITICAL: Disable all font kerning and OpenType features to prevent html2canvas spacing artifacts
  // font-kerning:none + font-feature-settings disables browser kerning adjustments
  const pageStyle = 'width:595px;min-width:595px;max-width:595px;background:#ffffff;'
    + 'font-family:Arial,Helvetica,sans-serif;margin:0;padding:0;box-sizing:border-box;'
    + 'overflow:hidden;position:relative;letter-spacing:0;word-spacing:0;'
    + 'white-space:normal;word-break:normal;overflow-wrap:normal;'
    + 'font-kerning:none;font-feature-settings:"kern" 0,"liga" 0;'
    + '-webkit-font-smoothing:none;text-rendering:geometricPrecision;';
  const headerStyle = `background:${primaryColor};padding:20px;margin:0 -20px 0 -20px;`
    + 'box-sizing:border-box;width:595px;min-width:595px;max-width:595px;position:relative;'
    + 'z-index:10;page-break-inside:avoid !important;break-inside:avoid !important;'
    + '-webkit-column-break-inside:avoid !important;page-break-after:avoid !important;'
    + 'break-after:avoid !important;break-before:avoid !important;'
    + 'letter-spacing:0;word-spacing:0;font-kerning:none;'
    + '';
  const flexRowStyle = 'display:flex;justify-content:space-between;align-items:center;'
    + 'margin-bottom:16px;position:relative;z-index:10;page-break-inside:avoid !important;'
    + 'break-inside:avoid !important;';
  const flexItemStyle = 'display:flex;align-items:center;position:relative;z-index:10;'
    + 'page-break-inside:avoid !important;break-inside:avoid !important;';
  const titleStyle = 'font-size:18px;font-weight:bold;color:#ffffff !important;'
    + 'vertical-align:middle;font-family:Arial,Helvetica,sans-serif;position:relative;z-index:10;'
    + 'letter-spacing:0;word-spacing:0;font-kerning:none;'
    + 'white-space:normal;word-break:normal;overflow-wrap:normal;';
  const tableStyle = 'width:100%;border-collapse:collapse;margin:0;padding:0;position:relative;'
    + 'z-index:10;page-break-inside:avoid !important;break-inside:avoid !important;';
  const trStyle = 'page-break-inside:avoid !important;break-inside:avoid !important;';
  const tdStyle = 'padding-right:48px;vertical-align:top;position:relative;z-index:10;'
    + 'page-break-inside:avoid !important;break-inside:avoid !important;';
  const tdLastStyle = 'vertical-align:top;position:relative;z-index:10;'
    + 'page-break-inside:avoid !important;break-inside:avoid !important;';
  const labelStyle = 'font-size:9px;font-weight:600;color:#D4D4D8 !important;margin:0;'
    + 'padding:0;line-height:1;margin-bottom:4px;font-family:Arial,Helvetica,sans-serif;'
    + 'position:relative;z-index:10;letter-spacing:0;word-spacing:0;font-kerning:none;'
    + 'white-space:normal;word-break:normal;overflow-wrap:normal;';
  const labelSpacingStyle = 'font-size:9px;font-weight:600;color:#D4D4D8 !important;margin:0;'
    + 'padding:0;line-height:1;margin-bottom:4px;'
    + 'font-family:Arial,Helvetica,sans-serif;position:relative;z-index:10;letter-spacing:0;word-spacing:0;font-kerning:none;'
    + 'white-space:normal;word-break:normal;overflow-wrap:normal;';
  const valueStyle = 'font-size:12px;font-weight:600;color:#ffffff !important;margin:0;'
    + 'padding:0;line-height:1;font-family:Arial,Helvetica,sans-serif;position:relative;z-index:10;'
    + 'letter-spacing:0;word-spacing:0;font-kerning:none;'
    + 'white-space:normal;word-break:normal;overflow-wrap:normal;';
  const statusDivStyle = 'margin:0;padding:0;line-height:1;position:relative;z-index:10;';
  const contentStyle = 'padding:0 20px;margin:0;box-sizing:border-box;width:595px;'
    + 'min-width:595px;max-width:595px;';

  return `
    <div id="form-submission-pdf" class="pdf-page" style="${pageStyle}">
      <div class="pdf-header" style="${headerStyle}">
        <div style="${flexRowStyle}">
          <div style="${flexItemStyle}">
            ${formIconHtml}
            <span style="${titleStyle}">${escapeHtml(form.title)}</span>
          </div>
          <div style="${flexItemStyle}">
            ${orgLogoHtml}
          </div>
        </div>
        <table style="${tableStyle}">
          <tr style="${trStyle}">
            <td style="${tdStyle}">
              <div style="${labelStyle}">${t('forms:name', 'Name')}</div>
              <div style="${valueStyle}">${escapeHtml(userName)}</div>
            </td>
            <td style="${tdStyle}">
              <div style="${labelSpacingStyle}">
                ${submittedAtText}
              </div>
              <div style="${valueStyle}">${formattedDate}</div>
            </td>
            <td style="${tdLastStyle}">
              <div style="${labelStyle}">${t('forms:status', 'Status')}</div>
              <div style="${statusDivStyle}">${statusHtml}</div>
            </td>
          </tr>
        </table>
      </div>
      <div class="pdf-content" style="${contentStyle}">
        ${answersHtml}
      </div>
    </div>
  `;
}

export async function exportFormSubmissionPDF(options: ExportFormSubmissionPDFOptions): Promise<void> {
  const { form, user, org, submission } = options;
  const t = (key: string, fallback?: string) => i18n.t(key, fallback) as string;

  // Load images
  let orgLogoDataURL: string | null = null;
  let formIconDataURL: string | null = null;
  let footerLogoDataURL: string | null = null;

  if (org.brand_icon) {
    orgLogoDataURL = await convertImageToBase64(org.brand_icon) || await imageUrlToDataUrl(org.brand_icon) || null;
  }

  if (form.icon) {
    const iconUrl = form.icon.startsWith('https://') ? form.icon : `https://assets.oneteam.io/globals/attachments/${form.icon}`;
    formIconDataURL = await convertImageToBase64(iconUrl) || await imageUrlToDataUrl(iconUrl) || null;
  }

  footerLogoDataURL = await convertImageToBase64(ONETEAM_LOGO) || await imageUrlToDataUrl(ONETEAM_LOGO) || null;

  // Get logo aspect ratio
  let logoAspectRatio = 1;
  if (footerLogoDataURL) {
    const img = new Image();
    img.src = footerLogoDataURL;
    await new Promise<void>((resolve) => {
      if (img.complete) {
        if (img.naturalWidth && img.naturalHeight) {
          logoAspectRatio = img.naturalWidth / img.naturalHeight;
        }
        resolve();
      } else {
        img.onload = () => {
          if (img.naturalWidth && img.naturalHeight) {
            logoAspectRatio = img.naturalWidth / img.naturalHeight;
          }
          resolve();
        };
        img.onerror = () => resolve();
        setTimeout(() => resolve(), 2000);
      }
    });
  }

  // Preload question icons
  const iconDataUrls: Record<string, string | null> = {};
  const uniqueQuestionTypes = new Set(
    (submission.answers || []).map((answer) => answer.question?.type).filter(Boolean)
  );

  await Promise.all(
    Array.from(uniqueQuestionTypes).map(async (type) => {
      iconDataUrls[type as string] = await getQuestionIconDataUrl(type as string);
    })
  );

  const checklistTickDataUrl = await getChecklistTickDataUrl();

  // Preload image attachments and convert to data URLs
  const imageDataUrlMap = new Map<string, string | null>();
  const fileUploadAnswers = (submission.answers || []).filter(
    (answer) => answer.question?.type === EQuestionTypes.FILE_UPLOAD
  );

  await Promise.all(
    fileUploadAnswers.flatMap((answer) => {
      if (!Array.isArray(answer.answer)) return [];
      return (answer.answer as any[]).map(async (attachment: any) => {
        const attachmentId = attachment.id || '';
        if (!attachmentId) {
          return;
        }

        const fileType = attachment.file_type || '';
        const isImage = fileType && (fileType.startsWith('image') || fileType === 'image' || fileType === 'gif');

        if (!isImage) {
          return;
        }

        // Get image URL - prioritize thumb_path, then path, then download_path
        let imageUrl: string | null = null;
        const rawUrl = attachment.thumb_path || attachment.path || attachment.download_path || '';
        if (rawUrl) {
          // If it's already a full URL, use it; otherwise construct it
          if (rawUrl.startsWith('http://') || rawUrl.startsWith('https://')) {
            imageUrl = rawUrl;
          } else if (rawUrl.startsWith('/')) {
            imageUrl = `https://dev-assets.oneteam.io${rawUrl}`;
          } else {
            imageUrl = `https://dev-assets.oneteam.io/${rawUrl}`;
          }
        }

        if (!imageUrl) {
          return;
        }

        // Convert image to data URL
        const imageDataUrl = await convertImageToBase64(imageUrl) || await imageUrlToDataUrl(imageUrl) || null;

        if (imageDataUrl) {
          imageDataUrlMap.set(attachmentId, imageDataUrl);
        }
      });
    })
  );

  // First, generate question blocks HTML without page breaks
  const answersHtmlParts: string[] = [];

  (submission.answers || []).forEach((answer, index) => {
    const { question } = answer;
    if (!question) return;

    const questionColor = getQuestionColor(question.type);
    const iconDataUrl = iconDataUrls[question.type];
    const questionText = question.parameters?.text || t('survey:survey_response_question_no_text', 'No question text');
    const questionDescription = question.parameters?.description || '';
    const answerHtml = formatAnswerForHTML(answer, t, checklistTickDataUrl, imageDataUrlMap, options.timezone);

    // Reduced icon size to match web UI proportions
    const iconContainerStyle = `display:inline-block;width:20px;height:20px;background:${questionColor};`
      + 'border-radius:4px;vertical-align:top;margin-right:8px;position:relative;'
      + 'overflow:hidden;flex-shrink:0;page-break-inside:avoid;break-inside:avoid;';
    const iconImgStyle = 'position:absolute;top:50%;left:50%;transform:translate(-50%,-50%);'
      + 'width:12px;height:12px;display:block;page-break-inside:avoid;break-inside:avoid;'
      + 'image-rendering:auto;object-fit:contain;';
    const iconPlaceholderStyle = `display:inline-block;width:20px;height:20px;background:${questionColor};`
      + 'border-radius:4px;text-align:center;line-height:20px;font-size:11px;color:white;'
      + 'vertical-align:top;margin-right:8px;flex-shrink:0;'
      + 'page-break-inside:avoid;break-inside:avoid;';
    const iconHtml = iconDataUrl
      ? `<span style="${iconContainerStyle}">
          <img src="${iconDataUrl}" style="${iconImgStyle}" alt="" />
        </span>`
      : `<span style="${iconPlaceholderStyle}">?</span>`;

    const descriptionStyle = 'font-size:9px;font-weight:500;color:#52525B;'
      + 'word-wrap:break-word;overflow-wrap:break-word;margin-top:2px;line-height:1.3;'
      + 'letter-spacing:0;word-spacing:0;font-kerning:none;'
      + 'white-space:normal;word-break:normal;';
    const descriptionHtml = questionDescription
      ? `<div style="${descriptionStyle}">${escapeHtml(questionDescription)}</div>`
      : '';

    // Reduced line-height and spacing for more compact text
    // font-kerning:none disables browser kerning to prevent html2canvas spacing artifacts
    const questionTextStyle = 'font-size:12px;font-weight:600;color:#27272A;line-height:1.4;'
      + 'word-wrap:break-word;overflow-wrap:break-word;letter-spacing:0;word-spacing:0;'
      + 'font-kerning:none;white-space:normal;word-break:normal;';
    const answerTextStyle = 'font-size:10px;font-weight:500;color:#09090B;'
      + 'word-wrap:break-word;overflow-wrap:break-word;line-height:1.4;'
      + 'letter-spacing:0;word-spacing:0;font-kerning:none;white-space:normal;word-break:normal;';

    const isFileUpload = question.type === EQuestionTypes.FILE_UPLOAD;
    // For file uploads, allow page breaks inside (for multiple attachments) but prevent individual items from breaking
    // CRITICAL: padding must be maintained on both sides when breaking across pages
    // Aligned padding with other form items for consistency
    const answerWrapperStyle = isFileUpload
      ? 'background:#F4F4F5;border-radius:8px;padding:10px;padding-top:10px;'
        + 'padding-bottom:10px;padding-left:10px;padding-right:10px;margin-top:6px;'
      : 'background:#F4F4F5;border-radius:8px;padding:10px;margin-top:6px;';

    const isLastBlock = index === (submission.answers || []).length - 1;
    // For file upload questions: hide border on wrapper if it has answers (border will be on last answer box instead)
    // For non-file upload questions: show border on wrapper if not last
    // File upload with answers: no border on wrapper (border will be on last answer box instead)
    // Non-file upload or file upload without answers: normal border logic
    const hasFileUploadAnswers = isFileUpload
      && (Array.isArray(answer.answer) && answer.answer.length > 0);
    const borderBottom = hasFileUploadAnswers
      ? '' // File upload with answers: no border on wrapper
      : (isLastBlock ? '' : 'border-bottom:1px solid #F4F4F5;');

    // For non-FILE_UPLOAD blocks: add page-break prevention to wrapper
    // The cloning logic will apply these styles recursively to all children
    const wrapperPageBreakStyles = !isFileUpload
      ? 'page-break-inside:avoid !important;break-inside:avoid !important;'
        + '-webkit-column-break-inside:avoid !important;orphans:999;widows:999;'
      : '';
    const wrapperStyles = 'display:block;page-break-inside:avoid;break-inside:avoid;margin-top:0;';
    // Wrap everything in a single atomic container - padding included inside to prevent splitting
    // For non-FILE_UPLOAD: Always use page-break-inside:avoid to prevent cutting across pages
    // Always use padding-top and padding-bottom to ensure consistent spacing even on new pages
    // Reduced vertical padding for more compact layout
    // For file upload questions with attachments: remove padding-bottom (spacing handled by file items)
    const paddingBottom = hasFileUploadAnswers ? '0' : '16px';
    const questionBlockHtml = `
      <div class="qa-block-wrapper" data-block-index="${index}" 
        style="display:block;width:100%;${borderBottom}${wrapperPageBreakStyles ? ';' + wrapperPageBreakStyles : ''}">
        <div style="${wrapperStyles}padding-top:16px;padding-bottom:${paddingBottom};">
          <div style="display:flex;align-items:flex-start;">
            ${iconHtml}
            <div style="flex:1;min-width:0;">
              <div style="${questionTextStyle}">${escapeHtml(questionText)}</div>
              ${descriptionHtml ? `<div style="${descriptionStyle}">${escapeHtml(questionDescription)}</div>` : ''}
              <div class="answer-wrapper" style="${answerWrapperStyle}word-wrap:break-word;overflow-wrap:break-word;">
                <div style="${answerTextStyle}">${answerHtml}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    `;

    answersHtmlParts.push(questionBlockHtml);
  });

  const answersHtml = answersHtmlParts.join('');
  const htmlContent = generateSubmissionHTML({
    options,
    orgLogoDataURL: orgLogoDataURL || undefined,
    formIconDataURL: formIconDataURL || undefined,
    answersHtml,
  });

  // Create container and render
  const container = document.createElement('div');
  container.innerHTML = htmlContent;
  container.style.cssText = 'position: fixed; top: 0; left: -9999px; width: 595px; '
    + 'min-width: 595px; max-width: 595px; z-index: -1; background: #ffffff; margin: 0; '
    + 'padding: 0; overflow: hidden; box-sizing: border-box;';
  document.body.appendChild(container);

  const element = container.firstElementChild as HTMLElement;
  if (!element) {
    document.body.removeChild(container);
    throw new Error('Failed to create submission element');
  }

  // Wait for images to load
  const initialImages = container.querySelectorAll('img');
  await Promise.all(
    Array.from(initialImages).map(
      (imgElement) => new Promise<void>((resolve) => {
          if (imgElement.complete) {
            resolve();
            return;
          }
          const onLoad = () => resolve();
          const onError = () => resolve();
          imgElement.addEventListener('load', onLoad);
          imgElement.addEventListener('error', onError);
          setTimeout(() => {
            imgElement.removeEventListener('load', onLoad);
            imgElement.removeEventListener('error', onError);
            resolve();
          }, 3000);
        })
    )
  );

  // Wait for all images and layout to settle
  await new Promise((r) => setTimeout(r, 500));
  void element.offsetHeight;

  // Measure header height - CRITICAL: Header height must be measured accurately
  // The header contains username, form title, submission date, etc. and takes up space on first page
  // This height MUST be accounted for in first page's usedHeight to prevent content miscalculation
  const header = element.querySelector('.pdf-header') as HTMLElement;
  if (!header) {
    throw new Error('Could not find PDF header');
  }

  // Force multiple layout calculation passes for accurate header height measurement
  void header.offsetHeight;
  void header.scrollHeight;
  await new Promise((r) => setTimeout(r, 100));
  void header.offsetHeight;
  void header.scrollHeight;
  await new Promise((r) => setTimeout(r, 50));
  void header.offsetHeight;

  // Measure header height using max of all measurement methods for accuracy
  // This ensures we capture the full height including all padding, margins, and content
  const headerRect = header.getBoundingClientRect();
  const headerScrollHeight = header.scrollHeight;
  const headerOffsetHeight = header.offsetHeight;
  const headerHeight = Math.ceil(
    Math.max(headerRect.height, headerScrollHeight, headerOffsetHeight)
  );

  // Calculate usable page height: PAGE_HEIGHT - FOOTER_HEIGHT
  // CRITICAL: For first page, headerHeight is accounted for in usedHeight
  // For continuation pages, full USABLE_HEIGHT is available (no header)
  const USABLE_HEIGHT = PAGE_HEIGHT - FOOTER_HEIGHT;
  const SAFETY_MARGIN = 20; // Larger buffer for jsPDF rendering differences and rounding errors

  // Get all question block wrappers
  const qaBlockWrappers = Array.from(element.querySelectorAll('.qa-block-wrapper') as NodeListOf<HTMLElement>);

  // Wait for layout to settle before measuring
  await new Promise((r) => setTimeout(r, 200));
  void element.offsetHeight;
  qaBlockWrappers.forEach((wrapper) => {
    void wrapper.offsetHeight;
  });
  await new Promise((r) => setTimeout(r, 100));

  // PAGE-BY-PAGE RENDERING: Each PDF page is rendered independently
  //
  // CRITICAL: Shared-DOM pagination with html2canvas is fundamentally unreliable.
  // Spacers appear mid-page, images are cut, page breaks leak across pages.
  //
  // SOLUTION: Build pages as arrays of atomic elements, render ONE PAGE AT A TIME.
  //
  // This ensures:
  // - No shared DOM container (each page is isolated)
  // - No spacers (CSS padding only)
  // - No page-break divs (page boundaries are enforced by separate rendering)
  // - No content leaks between pages (each page is rendered independently)
  // - Deterministic output (page 1 does not affect page 2)
  //
  // Build logical pages as arrays of elements (OFF-DOM)
  const logicalPages: HTMLElement[][] = [];
  let currentPage: HTMLElement[] = [];
  let usedHeight = 0; // Track height for current logical page

  /**
   * Creates a fresh page container for rendering a single PDF page.
   * CRITICAL: Each page is rendered independently - no shared DOM, no page breaks, no spacers.
   *
   * @param isFirstPage - Whether this is the first page (has header, no top padding)
   * @returns Fresh page container with appropriate padding
   */
  function createFreshPageContainer(isFirstPage: boolean): HTMLDivElement {
    const pageContainer = document.createElement('div');
    pageContainer.className = 'pdf-page-container';

    // Top spacing = static padding (NOT a spacer div)
    // First page: no top padding (header provides spacing)
    // Continuation pages: padding-top for visual spacing
    const paddingTop = isFirstPage ? '0' : `${PAGE_TOP_MARGIN}px`;

    // CRITICAL: Disable all font kerning and OpenType features to prevent html2canvas spacing artifacts
    // font-kerning:none + font-feature-settings disables browser kerning adjustments
    pageContainer.style.cssText = `
      width: 555px;
      padding: ${paddingTop} 20px 0 20px;
      box-sizing: border-box;
      background: #ffffff;
      font-family: Arial, Helvetica, sans-serif;
      letter-spacing: 0;
      word-spacing: 0;
      white-space: normal;
      word-break: normal;
      overflow-wrap: normal;
      font-kerning: none;
      font-feature-settings: "kern" 0, "liga" 0;
      -webkit-font-smoothing: none;
      text-rendering: geometricPrecision;
    `;

    return pageContainer;
  }

  /**
   * Waits for all images in an element to load.
   * MANDATORY: Images must be loaded BEFORE measurement to prevent late reflow.
   *
   * @param el - Element containing images
   */
  async function waitForImages(el: HTMLElement): Promise<void> {
    const imageElements = el.querySelectorAll('img');
    if (imageElements.length === 0) {
      return;
    }

    await Promise.all(
      Array.from(imageElements).map(
        (imgElement) => new Promise<void>((resolve) => {
            // If image is already complete, resolve immediately
            if (imgElement.complete && imgElement.naturalHeight > 0 && imgElement.naturalWidth > 0) {
              resolve();
              return;
            }
            // Wait for image to load - create handlers without mutating parameter
            const onLoad = () => resolve();
            const onError = () => resolve(); // Resolve even on error to prevent hanging
            imgElement.addEventListener('load', onLoad);
            imgElement.addEventListener('error', onError);
            // Timeout after 3 seconds
            setTimeout(() => {
              imgElement.removeEventListener('load', onLoad);
              imgElement.removeEventListener('error', onError);
              resolve();
            }, 3000);
          })
      )
    );
  }

  /**
   * Measures element height OFF-DOM (NO DOM SIDE EFFECTS).
   * CRITICAL: Elements are measured in isolation, not on actual pages.
   * Only real content elements are measured - control nodes are never measured.
   *
   * @param el - Element to measure (content element only)
   * @param sandbox - Temporary container for measurement (must be hidden, positioned off-screen)
   * @returns Final measured height
   */
  async function measureElement(el: HTMLElement, sandbox: HTMLElement): Promise<number> {
    // MANDATORY: Wait for images to load BEFORE measurement
    await waitForImages(el);

    sandbox.appendChild(el);

    // Wait for next frame to ensure layout is stable after images load
    await new Promise((r) => setTimeout(r, 50));
    void el.offsetHeight; // Force layout calculation

    // Measure height using max of all measurement methods for accuracy
    const height = Math.ceil(
      Math.max(
        el.getBoundingClientRect().height,
        el.scrollHeight,
        el.offsetHeight
      )
    );

    sandbox.removeChild(el);
    return height;
  }

  /**
   * Adds an element to the current logical page.
   * CRITICAL: Page-by-page rendering - elements are stored in arrays, not DOM.
   * If element doesn't fit, starts a new logical page.
   *
   * @param el - Element to add (must be measured first)
   * @param height - Measured height of the element
   */
  function addElementToCurrentPage(el: HTMLElement, height: number): void {
    // Check if element fits on current logical page
    // Account for header height on first page
    const availableHeight = logicalPages.length === 0
      ? USABLE_HEIGHT - headerHeight // First page: account for header
      : USABLE_HEIGHT; // Continuation pages: full height

    const requiredSpace = height + SAFETY_MARGIN;

    // If element doesn't fit, start new logical page
    if (usedHeight + requiredSpace > availableHeight) {
      // Save current page and start new one
      logicalPages.push(currentPage);
      currentPage = [];
      usedHeight = 0;
    }

    // Add element to current logical page
    currentPage.push(el);
    usedHeight += height;
  }


  // Create measurement sandbox for OFF-DOM measurements
  // CRITICAL: Elements are measured in isolation, not on actual pages (NO DOM SIDE EFFECTS)
  const measureSandbox = document.createElement('div');
  measureSandbox.style.cssText = `
    width: 555px;
    padding: 0 20px;
    box-sizing: border-box;
    position: absolute;
    top: -9999px;
    left: -9999px;
    overflow: visible;
    visibility: visible;
  `;
  document.body.appendChild(measureSandbox);

  // Process each question block - build logical pages as arrays of elements
  // eslint-disable-next-line no-await-in-loop
  for (let blockIndex = 0; blockIndex < qaBlockWrappers.length; blockIndex++) {
    const wrapper = qaBlockWrappers[blockIndex];
    void wrapper.offsetHeight;
    const isFileUploadBlock = !!wrapper.querySelector('.file-item-wrapper, .file-name-item, .file-items-container');

    if (!isFileUploadBlock) {
      // NON-FILE_UPLOAD: Entire block is ONE atomic element
      const clonedBlock = wrapper.cloneNode(true) as HTMLElement;

      // Measure element OFF-DOM
      // eslint-disable-next-line no-await-in-loop
      const height = await measureElement(clonedBlock, measureSandbox);

      // Add to current logical page (creates new page if needed)
      addElementToCurrentPage(clonedBlock, height);

    } else {
      // FILE_UPLOAD: Question header stays together, but file items can break across pages
      const questionHeader = wrapper.querySelector('[style*="display:flex"]')?.parentElement as HTMLElement;

      if (!questionHeader) {
        // Fallback: treat as non-FILE_UPLOAD - entire block is ONE atomic element
        const clonedBlock = wrapper.cloneNode(true) as HTMLElement;

        // Measure element OFF-DOM
        // eslint-disable-next-line no-await-in-loop
        const height = await measureElement(clonedBlock, measureSandbox);

        // Add to current logical page (creates new page if needed)
        addElementToCurrentPage(clonedBlock, height);
        continue;
      }

      // Render question header atomically (only once)
      // Question header must stay together and never split


      // Find file items in the original wrapper
      const fileItemsContainer = wrapper.querySelector('.file-items-container') as HTMLElement;
      if (!fileItemsContainer) {
        // No file items - treat entire wrapper as ONE atomic element
        const fileUploadWrapper = wrapper.cloneNode(true) as HTMLElement;

        // Measure element OFF-DOM
        // eslint-disable-next-line no-await-in-loop
        const height = await measureElement(fileUploadWrapper, measureSandbox);

        // Add to current logical page (creates new page if needed)
        addElementToCurrentPage(fileUploadWrapper, height);
        continue;
      }

      const fileItems = Array.from(fileItemsContainer.querySelectorAll('.file-item-wrapper, .file-name-item')) as HTMLElement[];
      const originalAnswerWrapper = wrapper.querySelector('.answer-wrapper, [style*="background:#F4F4F5"]') as HTMLElement;

      if (!originalAnswerWrapper) {
        // No answer wrapper found - treat entire wrapper as ONE atomic element
        const fileUploadWrapper = wrapper.cloneNode(true) as HTMLElement;

        // Measure element OFF-DOM
        // eslint-disable-next-line no-await-in-loop
        const height = await measureElement(fileUploadWrapper, measureSandbox);

        // Add to current logical page (creates new page if needed)
        addElementToCurrentPage(fileUploadWrapper, height);
        continue;
      }

      // Helper function to create a separate answer wrapper box for a single file item
      // We'll measure it after adding to the actual page context (like non-FILE_UPLOAD questions)
      // Add left margin to align with other answer boxes (icon width 20px + margin 8px = 28px)
      const createFileItemBox = (fileItem: HTMLElement): HTMLElement => {
        const answerWrapper = document.createElement('div');
        answerWrapper.className = 'answer-wrapper';
        answerWrapper.style.cssText = `
          background:#F4F4F5;
          border-radius:8px;
          padding:10px;
          margin-top:6px;
          margin-left:28px;
          box-sizing:border-box;
          page-break-inside:avoid !important;
          break-inside:avoid !important;
          -webkit-column-break-inside:avoid !important;
          page-break-before:auto !important;
          break-before:auto !important;
          page-break-after:auto !important;
          break-after:auto !important;
          orphans:999 !important;
          widows:999 !important;
          width:calc(100% - 28px);
          display:block;
        `;

        const fileItemsContainerInner = document.createElement('div');
        fileItemsContainerInner.className = 'file-items-container';
        fileItemsContainerInner.style.cssText = `
          padding:0;
          margin:0;
          page-break-inside:avoid !important;
          break-inside:avoid !important;
          -webkit-column-break-inside:avoid !important;
        `;

        const clonedFileItem = fileItem.cloneNode(true) as HTMLElement;
        // Ensure file item also has page-break-inside:avoid and all children
        clonedFileItem.style.setProperty('page-break-inside', 'avoid', 'important');
        clonedFileItem.style.setProperty('break-inside', 'avoid', 'important');
        clonedFileItem.style.setProperty('-webkit-column-break-inside', 'avoid', 'important');
        clonedFileItem.style.setProperty('orphans', '999', 'important');
        clonedFileItem.style.setProperty('widows', '999', 'important');

        // CRITICAL: Remove ALL bottom margins from file item and ALL nested elements recursively
        // This prevents extra spacing at the bottom of the box
        // Each file upload box contains only ONE file item, so no bottom margin is needed
        clonedFileItem.style.setProperty('margin-bottom', '0', 'important');

        // Apply page-break styles AND remove margin-bottom from ALL children recursively
        const allChildren = clonedFileItem.querySelectorAll('*');
        allChildren.forEach((child) => {
          const childEl = child as HTMLElement;
          childEl.style.setProperty('page-break-inside', 'avoid', 'important');
          childEl.style.setProperty('break-inside', 'avoid', 'important');
          childEl.style.setProperty('-webkit-column-break-inside', 'avoid', 'important');
          // CRITICAL: Remove bottom margin from all nested elements to prevent extra spacing
          childEl.style.setProperty('margin-bottom', '0', 'important');
        });

        fileItemsContainerInner.appendChild(clonedFileItem);
        answerWrapper.appendChild(fileItemsContainerInner);

        return answerWrapper;
      };

      // Create question block wrapper (without answer wrapper) - only header
      const createQuestionHeaderOnly = (): HTMLElement => {
        const questionBlockWrapper = wrapper.cloneNode(false) as HTMLElement;
        // Find the inner padding div - look for padding-top:16px (updated from 20px)
        // Also check for the display:flex div that contains the question content
        const innerPaddingDiv = wrapper.querySelector('div[style*="padding-top:16px"]') as HTMLElement
          || wrapper.querySelector('div[style*="display:flex"]')?.parentElement as HTMLElement;

        if (innerPaddingDiv) {
          const clonedInnerDiv = innerPaddingDiv.cloneNode(true) as HTMLElement;

          // Remove the answer wrapper from the cloned structure (we only want question header)
          const clonedAnswerWrapper = clonedInnerDiv.querySelector(
            '.answer-wrapper, [style*="background:#F4F4F5"]'
          ) as HTMLElement;
          if (clonedAnswerWrapper && clonedAnswerWrapper.parentNode) {
            clonedAnswerWrapper.parentNode.removeChild(clonedAnswerWrapper);
          }

          // For file upload questions with attachments: remove padding-bottom from question header
          // (spacing is handled by the file item boxes below)
          const currentStyle = clonedInnerDiv.getAttribute('style') || '';
          if (currentStyle.includes('padding-bottom')) {
            const updatedStyle = currentStyle.replace(/padding-bottom:\d+px/g, 'padding-bottom:0');
            clonedInnerDiv.setAttribute('style', updatedStyle);
          }

          questionBlockWrapper.appendChild(clonedInnerDiv);
        } else {
          // Fallback: clone the entire wrapper but remove answer content
          const fallbackClone = wrapper.cloneNode(true) as HTMLElement;
          const answerWrapper = fallbackClone.querySelector('.answer-wrapper, [style*="background:#F4F4F5"]') as HTMLElement;
          if (answerWrapper && answerWrapper.parentNode) {
            answerWrapper.parentNode.removeChild(answerWrapper);
          }
          return fallbackClone;
        }

        return questionBlockWrapper;
      };

      // REMOVED: lockFileUploadBoxHeight function - replaced with preflightMeasure
      // All measurements now happen via preflightMeasure before rendering
      // This ensures page-break decisions happen BEFORE any element is appended

      // Check if this is the last question overall
      const isLastQuestion = blockIndex === qaBlockWrappers.length - 1;

      // Track if question header has been added
      let questionHeaderAdded = false;

      // Process each file item - each file upload answer box is ONE atomic element
      // CRITICAL: File-upload answer box = ONE ATOM - measure once, never split
      for (let fileItemIndex = 0; fileItemIndex < fileItems.length; fileItemIndex++) {
        const fileItem = fileItems[fileItemIndex];
        const isLastFileItem = fileItemIndex === fileItems.length - 1;

        // Create the answer wrapper box
        const fileItemBox = createFileItemBox(fileItem);

        // Check if we need to add question header first (only once, before first file item)
        if (!questionHeaderAdded) {
          const questionHeaderEl = createQuestionHeaderOnly();

          // Measure question header OFF-DOM
          // eslint-disable-next-line no-await-in-loop
          const questionHeaderHeight = await measureElement(questionHeaderEl, measureSandbox);

          // Add question header to current logical page (creates new page if needed)
          addElementToCurrentPage(questionHeaderEl, questionHeaderHeight);

          questionHeaderAdded = true;
        }

        // CRITICAL: File-upload answer box = ONE ATOM
        // Measure ONCE, add to page ONCE, Never split
        // Measure element OFF-DOM
        // eslint-disable-next-line no-await-in-loop
        const height = await measureElement(fileItemBox, measureSandbox);

        // Account for margin-top (6px) on answer wrapper - it takes up space on the page
        const answerWrapperMarginTop = 6;
        const boxHeight = height + answerWrapperMarginTop;

          // For the last file item, wrap it in a border wrapper if this is NOT the last question
          // This matches the border style used on non-file-upload questions (line 489)
          // Border wrapper is just a container - fileItemBox inside already has proper left margin
          if (isLastFileItem && !isLastQuestion) {
            const borderWrapper = document.createElement('div');
            borderWrapper.style.cssText = 'display:block;width:100%;'
              + 'border-bottom:1px solid #F4F4F5;padding-bottom:20px;';
            borderWrapper.appendChild(fileItemBox);

            // Measure the wrapper with border
            // eslint-disable-next-line no-await-in-loop
            const wrapperHeight = await measureElement(borderWrapper, measureSandbox);
            addElementToCurrentPage(borderWrapper, wrapperHeight);
          } else {
            // Add file item box directly to current logical page (creates new page if needed)
            addElementToCurrentPage(fileItemBox, boxHeight);
          }
      }
    }
  }

  // Don't forget to add the last page if it has content
  if (currentPage.length > 0) {
    logicalPages.push(currentPage);
  }

  // Clean up measurement sandbox (no longer needed after building logical pages)
  if (measureSandbox.parentNode) {
    document.body.removeChild(measureSandbox);
  }

  // Generate PDF - Use A4 format explicitly (A4 = 595.28 x 841.89 points)
  // eslint-disable-next-line new-cap
  const pdf = new jsPDF({
    orientation: 'portrait',
    unit: 'px',
    format: [595, PAGE_HEIGHT],
    hotfixes: ['px_scaling'],
  });

  // CRITICAL: Page-by-page rendering - each page is rendered independently
  // NO shared DOM container, NO spacers, NO page-break divs, NO sealed pages
  // Each logical page is built fresh, rendered to PDF, then destroyed
  for (let pageIndex = 0; pageIndex < logicalPages.length; pageIndex++) {
    const pageElements = logicalPages[pageIndex];
    const isFirstPage = pageIndex === 0;

    // Create fresh page container for this page (destroyed after rendering)
    const pageContainer = createFreshPageContainer(isFirstPage);

    // Add header to first page only
    if (isFirstPage && header) {
      const headerClone = header.cloneNode(true) as HTMLElement;
      pageContainer.appendChild(headerClone);
    }

    // Add all elements for this page (clone nodes to avoid DOM mutations)
    for (const el of pageElements) {
      pageContainer.appendChild(el.cloneNode(true));
    }

    // Add page container to DOM for rendering (temporary - will be removed after)
    container.appendChild(pageContainer);

    // Render this single page to PDF
    // Use await to ensure each page completes before rendering the next
    // eslint-disable-next-line no-await-in-loop
    await new Promise<void>((resolve, reject) => {
      pdf.html(pageContainer, {
        callback: () => {
          try {
            // Page container MUST be destroyed after rendering (no shared DOM)
            if (pageContainer.parentNode) {
              pageContainer.parentNode.removeChild(pageContainer);
            }
            resolve();
          } catch (error) {
            reject(error);
          }
        },
        x: 0,
        y: 0,
        width: 595,
        windowWidth: 595,
        margin: [0, 0, FOOTER_HEIGHT, 0], // Bottom margin for footer space
        autoPaging: false, // CRITICAL: Disable auto-paging - we render one page at a time
        html2canvas: {
          scale: 1,
          useCORS: true,
          allowTaint: true,
          backgroundColor: '#ffffff',
          logging: false,
          imageTimeout: 5000,
          svgRendering: false,
          letterRendering: true,
          foreignObjectRendering: false,
          windowHeight: PAGE_HEIGHT,
          // Fix html2canvas text spacing issue
          onclone: (clonedDoc: Document) => {
            // CRITICAL FIX: html2canvas renders text with extra spacing.
            // Apply negative letter-spacing to compensate for canvas rendering artifacts.
            // Also force a specific font that renders consistently in canvas.
            const allElements = clonedDoc.querySelectorAll('*');
            allElements.forEach((elementValue) => {
              if (elementValue instanceof HTMLElement) {
                const elementStyle = elementValue.style;
                // Negative letter-spacing compensates for canvas text rendering gaps
                elementStyle.setProperty('letter-spacing', '-0.05px');
                elementStyle.setProperty('word-spacing', '0px');
                // Force Arial which renders predictably in canvas
                elementStyle.setProperty('font-family', 'Arial, sans-serif');
                // Disable any font features that might cause spacing issues
                elementStyle.setProperty('font-kerning', 'none');
                elementStyle.setProperty('font-variant-ligatures', 'none');
                elementStyle.setProperty('text-rendering', 'geometricPrecision');
              }
            });

            // Normalize all whitespace in text nodes
            const walker = clonedDoc.createTreeWalker(
              clonedDoc.body,
              NodeFilter.SHOW_TEXT,
              null
            );
            let textNode = walker.nextNode();
            while (textNode) {
              if (textNode.textContent) {
                textNode.textContent = textNode.textContent
                  .replace(/[\u00A0\u2000-\u200B\u2028\u2029\uFEFF\u3000]/g, ' ')
                  .replace(/\s+/g, ' ');
              }
              textNode = walker.nextNode();
            }
          },
        },
      });
    });

    // Add new page for next iteration (except for last page)
    if (pageIndex < logicalPages.length - 1) {
      pdf.addPage();
    }
  }

  // Add footers to all pages
  const totalPages = pdf.getNumberOfPages();
  const actualPageHeight = pdf.internal.pageSize.getHeight();
  const footerY = actualPageHeight - FOOTER_HEIGHT + 20;
  const pageText = t('forms:page', 'Page');
  const ofText = t('forms:of', 'of');
  const footerFontSize = 8;
  const footerColor = { r: 82, g: 82, b: 91 };
  const isWhitelabel = !!org.whitelabel_config;

  for (let i = 1; i <= totalPages; i++) {
    pdf.setPage(i);
    pdf.setFontSize(footerFontSize);
    pdf.setTextColor(footerColor.r, footerColor.g, footerColor.b);

    // Only show "Powered by Oneteam" for non-whitelabel organisations
    if (!isWhitelabel) {
      const poweredByText = t('forms:powered_by', 'Powered by');
      const oneTeamText = t('common:one_team_logo', 'oneteam');
      const logoHeight = 12;
      const logoWidth = footerLogoDataURL ? logoHeight * logoAspectRatio : 0;

      const leftX = 32;
      pdf.text(poweredByText, leftX, footerY);

      let currentX = leftX;
      if (footerLogoDataURL) {
        try {
          const logoX = currentX + pdf.getTextWidth(poweredByText) + 6;
          pdf.addImage(footerLogoDataURL, 'PNG', logoX, footerY - 10, logoWidth, logoHeight);
          currentX = logoX + logoWidth;
        } catch {
          // Ignore logo errors
        }
      }

      if (footerLogoDataURL) {
        pdf.text(oneTeamText, currentX + 6, footerY);
      } else {
        pdf.text(oneTeamText, leftX + pdf.getTextWidth(poweredByText) + 6, footerY);
      }
    }

    pdf.text(`${pageText} ${i} ${ofText} ${totalPages}`, 563, footerY, { align: 'right' });
  }

  // Clean up container
  if (container.parentNode) {
    document.body.removeChild(container);
  }

  // Save PDF
  const sanitizedFormTitle = form.title.replace(/[^a-z0-9]/gi, '_');
  const sanitizedUserName = user?.full_name.replace(/[^a-z0-9]/gi, '_') || 'anonymous';
  const fileName = `${sanitizedFormTitle}_${sanitizedUserName}_submission.pdf`;
  pdf.save(fileName);
}
